import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { useDeliveryLocation } from '@/lib/deliveryLocation';
import { useAuth } from '@/lib/AuthContext';

const WishlistContext = createContext(null);

// One authenticated snapshot for every card/page. Late requests from a previous
// session must never overwrite the new account's wishlist or a completed write.
export const WishlistProvider = ({ children }) => {
  const point = useDeliveryLocation();
  const { user } = useAuth();
  const userId = user?.registration_restricted ? null : user?.id || null;
  const identity = useRef(userId);
  identity.current = userId;
  const generation = useRef(0);
  const locks = useRef(new Set());
  const [pending, setPending] = useState(new Set());
  const [snapshot, setSnapshot] = useState({ owner: null, products: [], loading: false, error: false });

  const refresh = useCallback(async () => {
    const request = ++generation.current;
    if (!userId) { setSnapshot({ owner: null, products: [], loading: false, error: false }); return; }
    setSnapshot((s) => ({ owner: userId, products: s.owner === userId ? s.products : [], loading: true, error: false }));
    try {
      const { data } = await api.get('/wishlist', { params: { latitude: point?.latitude, longitude: point?.longitude } });
      if (request === generation.current && identity.current === userId) setSnapshot({ owner: userId, products: data, loading: false, error: false });
    } catch {
      if (request === generation.current && identity.current === userId) setSnapshot({ owner: userId, products: [], loading: false, error: true });
    }
  }, [userId, point?.latitude, point?.longitude]);
  useEffect(() => {
    locks.current.clear(); setPending(new Set()); refresh();
    return () => { generation.current += 1; };
  }, [refresh]);

  const products = snapshot.owner === userId ? snapshot.products : [];
  const loading = !!userId && (snapshot.owner !== userId || snapshot.loading);
  const toggle = async (product) => {
    if (!userId || identity.current !== userId) throw new Error('Inicia sesión para guardar en Favoritos');
    if (loading || snapshot.error) throw new Error('No se pudo cargar tu lista. Reintenta antes de modificarla.');
    const key = `${userId}:${product.id}`;
    if (locks.current.has(key)) return null;
    locks.current.add(key);
    setPending(new Set(locks.current));
    const remove = products.some((p) => p.id === product.id);
    try {
      if (remove) await api.delete(`/wishlist/${product.id}`);
      else await api.post('/wishlist', { product_id: product.id });
      if (identity.current !== userId) return null;
      setSnapshot((s) => s.owner !== userId ? s : { ...s, products: remove ? s.products.filter((p) => p.id !== product.id) : [...s.products.filter((p) => p.id !== product.id), product] });
      return !remove;
    } finally {
      locks.current.delete(key);
      if (identity.current === userId) setPending(new Set(locks.current));
    }
  };

  return <WishlistContext.Provider value={{ products, loading, error: snapshot.owner === userId && snapshot.error, refresh, toggle, isPending: (id) => pending.has(`${userId}:${id}`) }}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('WishlistProvider is required');
  return context;
};
